import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, Button, Form } from "react-bootstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import { addSample, updateSample } from "./service/sample-service";
import { masterEquipment } from "../ManageEquipment/service/equipment-service";
import { useLoader } from "../../_helper/LoaderContext";

const selectStyles = {
    control: (provided) => ({
        ...provided,
        minHeight: "38px",
    }),
    multiValue: (styles) => ({
        ...styles,
        backgroundColor: "#1b255b",
    }),
    multiValueLabel: (styles) => ({
        ...styles,
        color: "#fff",
        fontWeight: 500,
    }),
    multiValueRemove: (styles) => ({
        ...styles,
        color: "#d32f2f",
        ":hover": {
            backgroundColor: "#ffcdd2",
            color: "white",
        },
    }),
};

const getEquipmentId = (item) => {
    if (!item) return null;
    if (typeof item === "string") return item;
    return item._id || item.equipmentId || item.value || null;
};

const getEquipmentLabel = (item) => {
    if (!item || typeof item === "string") return item || "";
    const name = item.name || "";
    const code = item.code ? ` (${item.code})` : "";
    return `${name}${code}`.trim() || item._id;
};

export default function AddSample({ handleClose, data, getList }) {
    const { t } = useTranslation();
    const { setLoading } = useLoader();
    const [sample, setSample] = useState("");
    const [error, setError] = useState("");
    const [equipmentOptions, setEquipmentOptions] = useState([]);
    const [selectedEquipment, setSelectedEquipment] = useState([]);

    useEffect(() => {
        fetchEquipmentMaster();
    }, []);

    useEffect(() => {
        if (data) {
            setSample(data.name || "");
            const existing = data.requiredEquipment || data.requiredEquipments || [];
            const selected = (Array.isArray(existing) ? existing : [])
                .map((item) => {
                    const value = getEquipmentId(item);
                    if (!value) return null;
                    return {
                        value,
                        label: getEquipmentLabel(item) || value,
                    };
                })
                .filter(Boolean);
            setSelectedEquipment(selected);
        } else {
            setSample("");
            setSelectedEquipment([]);
        }
        setError("");
    }, [data]);

    // When master options load, refresh labels for edit mode
    useEffect(() => {
        if (!equipmentOptions.length || !selectedEquipment.length) return;
        setSelectedEquipment((prev) =>
            prev.map((sel) => {
                const match = equipmentOptions.find((opt) => opt.value === sel.value);
                return match || sel;
            })
        );
    }, [equipmentOptions]);

    const fetchEquipmentMaster = async () => {
        try {
            const res = await masterEquipment();
            if (res.data.success === true) {
                const list = res.data.data || [];
                const options = (Array.isArray(list) ? list : []).map((item) => ({
                    value: item._id,
                    label: item.code ? `${item.name} (${item.code})` : item.name,
                }));
                setEquipmentOptions(options);
            }
        } catch (err) {
            console.log(err);
        }
    };

    const handleSubmit = async () => {
        if (!sample.trim()) {
            setError(t("Please enter sample"));
            return;
        }

        const form_data = new FormData();
        form_data.append("name", sample.trim());
        if (data) {
            form_data.append("sampleId", data._id);
        }
        selectedEquipment.forEach((item) => {
            form_data.append("requiredEquipment[]", item.value);
        });

        setLoading(true);
        try {
            const res = data
                ? await updateSample(form_data)
                : await addSample(form_data);

            if (res.data.success == true) {
                toast.success(res.data.message);
                getList();
                setError("");
                handleClose();
            }
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Modal.Body>
                <Form
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleSubmit();
                    }}
                >
                    <Form.Group className="mb-3" controlId="sample">
                        <Form.Label>
                            {t("Sample Name")}
                            <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                            type="text"
                            placeholder={t("Enter sample name")}
                            value={sample}
                            onChange={(e) => {
                                setSample(e.target.value);
                                setError("");
                            }}
                            isInvalid={!!error}
                        />
                        {error && (
                            <Form.Control.Feedback type="invalid">
                                {error}
                            </Form.Control.Feedback>
                        )}
                    </Form.Group>

                    <Form.Group controlId="requiredEquipment">
                        <Form.Label>{t("Required Equipment")}</Form.Label>
                        <Select
                            isMulti
                            isSearchable
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            options={equipmentOptions}
                            value={selectedEquipment}
                            onChange={(selected) => setSelectedEquipment(selected || [])}
                            placeholder={t("Select equipment...")}
                            classNamePrefix="react-select"
                            styles={selectStyles}
                        />
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="primary" onClick={handleSubmit}>
                    {data ? "Update" : "Create"}
                </Button>
                <Button variant="secondary" onClick={handleClose}>
                    Close
                </Button>
            </Modal.Footer>
        </>
    );
}
